/**
 * ⚠️ DEVELOPMENT/TESTING ONLY - DO NOT SHIP TO PRODUCTION ⚠️
 *
 * SystemTestingModal — developer tools for system-web.
 * Provides Data Mode (Demo/Live) toggle and theme selection.
 */

import { X, Database, Globe, Trash2 } from 'lucide-react';
import { useSystemAppMode } from '../contexts/SystemAppModeContext';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import { useSystemDb } from './SystemDBProvider';
import { destroySystemDb } from '@firesquirrel/local-db';

interface SystemTestingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SystemTestingModal({ isOpen, onClose }: SystemTestingModalProps) {
  const { mode, setMode } = useSystemAppMode();
  const { forceResync } = useSystemDb();

  const handleSetMode = (next: typeof mode) => {
    if (next === mode) return;
    setMode(next);
    if (next === 'live') {
      forceResync();
    }
  };
  const { themeId, setThemeId, themes } = useTheme();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Testing Tools</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Data Mode — Demo vs Live */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Data Mode</h3>
            <p className="text-xs text-gray-500 mb-3">
              Controls where services read/write data. Demo uses local IndexedDB only. Live calls the real system-api backed by Docker MongoDB.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleSetMode('demo')}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                  mode === 'demo'
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <Database size={20} className={mode === 'demo' ? 'text-orange-600' : 'text-gray-400'} />
                <div className="text-left">
                  <div className={`text-sm font-medium ${mode === 'demo' ? 'text-orange-900' : 'text-gray-900'}`}>Demo</div>
                  <div className="text-[10px] text-gray-500">Local IndexedDB only</div>
                </div>
              </button>
              <button
                onClick={() => handleSetMode('live')}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                  mode === 'live'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <Globe size={20} className={mode === 'live' ? 'text-green-600' : 'text-gray-400'} />
                <div className="text-left">
                  <div className={`text-sm font-medium ${mode === 'live' ? 'text-green-900' : 'text-gray-900'}`}>Live</div>
                  <div className="text-[10px] text-gray-500">API → Docker MongoDB</div>
                </div>
              </button>
            </div>
            {mode === 'live' && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-[10px] text-amber-800">
                  <strong>Requires:</strong> system-api running locally + Docker MongoDB on port 27017.
                </p>
              </div>
            )}
          </div>

          {/* Theme Selection */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">UI Theme</h3>
            <p className="text-xs text-gray-500 mb-3">
              Test different color schemes and visual styles for the interface
            </p>
            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {themes.map(theme => (
                <button
                  key={theme.id}
                  onClick={() => setThemeId(theme.id)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    themeId === theme.id
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-4 h-4 rounded-full border border-gray-300"
                      style={{ background: theme.accent }}
                    />
                    <span className={`text-xs font-medium ${
                      themeId === theme.id ? 'text-orange-900' : 'text-gray-900'
                    }`}>
                      {theme.name}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <div className="w-4 h-2 rounded" style={{ background: theme.pageBg }} />
                    <div className="w-4 h-2 rounded" style={{ background: theme.cardBg }} />
                    <div className="w-4 h-2 rounded" style={{ background: theme.sidebarBg }} />
                    <div className="w-4 h-2 rounded" style={{ background: theme.accent }} />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Info box */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> These settings are for testing purposes only.
              Data Mode and Theme both persist across page reloads (localStorage).
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
